// Build the coc provenance triple from a file the support agent verified out of
// band. The FILE IS NEVER TRANSMITTED — only these digests — matching coc
// decision A6 and how the Word/gdoc certifiers already behave.
//
// .docx gets exact_subject "normalized_text": format-independent, so a manually
// attached .docx exactly matches a gdoc-certified copy of the same document, and
// a third party recomputes it with only normalize() + SHA-256.
//
// Everything else gets "file_bytes". PDF text extraction is implementation-
// defined (pdf.js, pypdf, poppler and Acrobat disagree), so a normalized_text
// hash from a PDF would be a promise no third party could keep.
import { extractDocxText } from "./docxText";
import { canonicalText, simhash64 } from "./simhash64";

export interface ProvenancePayload {
  exact_hash: string;
  exact_alg: "sha256";
  exact_subject: "file_bytes" | "normalized_text";
  fuzzy_digest?: string;
  algorithm_id?: string;
  source_filename: string;
  source_bytes: number;
}

async function sha256Hex(data: BufferSource): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

const isDocx = (file: File): boolean => /\.docx$/i.test(file.name);

// `provenance_payload.MAX_FILENAME`. The filename is incidental metadata; a
// 300-character one must never be able to 400 the whole request and stop the
// work from being created at all. Truncated at both ends, same policy (the
// server truncates rather than rejecting too) so a non-browser client cannot
// hit a different rule (86bbqzjcf).
export const MAX_FILENAME = 255;

export async function buildProvenance(file: File): Promise<ProvenancePayload | null> {
  if (file.size === 0) return null;
  const base = {
    source_filename: file.name.slice(0, MAX_FILENAME),
    source_bytes: file.size,
    exact_alg: "sha256",
  } as const;

  if (isDocx(file)) {
    const text = await extractDocxText(file);
    const canonical = text === null ? "" : canonicalText(text);
    if (canonical) {
      const fp = simhash64(text!);
      return {
        ...base,
        exact_hash: await sha256Hex(new TextEncoder().encode(canonical)),
        exact_subject: "normalized_text",
        ...(fp ? { fuzzy_digest: fp.hash, algorithm_id: `${fp.algo}-v${fp.version}` } : {}),
      };
    }
    // Unparseable or text-free .docx: degrade to bytes rather than claim a
    // normalized_text hash for text we could not read.
  }

  return {
    ...base,
    exact_hash: await sha256Hex(await file.arrayBuffer()),
    exact_subject: "file_bytes",
  };
}
